import { useHelpPanel } from "@/context/HelpPanelContext";

/**
 * The persistent "?" bubble, fixed bottom-right, that opens the Help Center slide-over on the
 * current screen's view. Matches the design of record's `.hc-bubble`
 * (content/design/client-experience-prototype.html) and is Cora's future entry point (GFG-700).
 *
 * z-[150] sits above page content but below the campaign builder's customize drawers (200), the
 * MUI app bar/drawer (1100/1200), modals, popovers and the help scrim (1300), snackbars (1400) and
 * full-screen builder overlays (9000+), so fixed right-edge UI covers the bubble, not the reverse.
 *
 * While the panel is open it fades out but stays focusable: the panel's focus trap restores focus
 * here on close. `obscured` (something covering it) drops it from the tab order and the
 * accessibility tree instead.
 */
export function HelpBubble({ obscured = false }: { obscured?: boolean }) {
  const { isOpen, open } = useHelpPanel();

  return (
    <button
      type="button"
      onClick={() => open()}
      aria-label="Help"
      title="Help"
      aria-haspopup="dialog"
      aria-expanded={isOpen}
      aria-hidden={obscured || undefined}
      tabIndex={obscured ? -1 : undefined}
      className={`fixed right-[22px] bottom-[max(22px,env(safe-area-inset-bottom))] z-[150] grid size-[52px] cursor-pointer place-items-center rounded-full bg-rodeo-black font-lato text-2xl font-bold leading-none text-white shadow-[0_4px_16px_rgba(51,41,30,0.22)] transition-[translate,background-color,opacity] duration-240 ease-[cubic-bezier(0.22,0.61,0.36,1)] hover:-translate-y-px hover:bg-[#454545] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-dust-ink motion-reduce:transition-none print:hidden after:absolute after:right-[7px] after:-bottom-[3px] after:border-t-[9px] after:border-l-[12px] after:border-t-rodeo-black after:border-l-transparent after:content-[''] after:transition-[border-top-color] after:duration-240 after:ease-[cubic-bezier(0.22,0.61,0.36,1)] hover:after:border-t-[#454545] motion-reduce:after:transition-none ${
        isOpen ? "pointer-events-none translate-y-1 opacity-0" : ""
      }`}
    >
      ?
    </button>
  );
}
